import { Esqueleto } from '@persona/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useAtalho } from '../src/atalhos/useAtalho';
import { useSessao } from '../src/sessao/sessao';
import { conectarTempoReal } from '../src/tempoReal/signalr';
import { lojaAbas, useAbas } from './abas';
import { BarraAbas } from './BarraAbas';
import { PaletaGlobal } from './PaletaGlobal';
import { Topo } from './Topo';

// "O que mudou" só aparece depois de uma atualização: fica fora do JS inicial (orçamento 07 §6.1).
const AvisoNovidades = lazy(() => import('../src/novidades/Novidades').then((m) => ({ default: m.AvisoNovidades })));

/** Módulo do contexto atual: /m/:modulo ou o módulo da rotina aberta (vem do esquema, publicado pela página). */
export function moduloDaRota(caminho: string, moduloRotina: string | null): string | undefined {
  const m = /^\/m\/([a-z]{3})/i.exec(caminho);
  if (m) return m[1].toUpperCase();
  return caminho.startsWith('/r/') ? (moduloRotina ?? undefined) : undefined;
}

let moduloAtual: string | null = null;
const ouvintesModulo = new Set<(m: string | null) => void>();

/** Módulo do menu agora (contexto de onde o usuário vem ao abrir a próxima rotina). */
export function moduloContexto() {
  return moduloAtual;
}

/** A página de módulo ou de rotina avisa o módulo do contexto, para a barra de módulos marcar o certo. */
export function publicarModulo(m: string | null) {
  moduloAtual = m;
  ouvintesModulo.forEach((f) => f(m));
}

export function Shell() {
  const { sessao, carregando } = useSessao();
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [paleta, setPaleta] = useState<{ aberta: boolean; texto: string }>({ aberta: false, texto: '' });
  const [modulo, setModulo] = useState<string | null>(moduloAtual);
  const moduloAtivo = moduloDaRota(caminho, modulo);
  const { abas } = useAbas();

  useEffect(() => {
    ouvintesModulo.add(setModulo);
    return () => void ouvintesModulo.delete(setModulo);
  }, []);

  useEffect(() => {
    if (!sessao) return undefined;
    return conectarTempoReal(cliente);
  }, [sessao, cliente]);

  // Fora de /r/ nenhuma aba fica "ativa" (o indicador some).
  useEffect(() => {
    if (!caminho.startsWith('/r/')) lojaAbas.desativar();
  }, [caminho]);

  const abrirPaleta = (texto = '') => setPaleta({ aberta: true, texto });
  useEffect(() => {
    abrirPaletaExterna = (texto) => setPaleta({ aberta: true, texto });
    return () => {
      abrirPaletaExterna = null;
    };
  }, []);

  useAtalho({ id: 'global.paleta', teclas: ['Ctrl+K'], escopo: 'global', descricao: 'Buscar rotina, registro ou ação', executar: () => abrirPaleta() });
  useAtalho({ id: 'global.inicio', teclas: ['Alt+Home'], escopo: 'global', descricao: 'Ir para o início', executar: () => void navegar({ to: '/' }) });

  if (carregando) {
    return (
      <div className="shell-carregando" aria-busy="true">
        <Esqueleto linhas={4} />
      </div>
    );
  }
  if (!sessao) return <Navigate to="/entrar" search={{ volta: caminho === '/' ? undefined : caminho }} />;

  return (
    <div className="shell">
      <a className="pular" href="#conteudo">Pular para o conteúdo</a>
      <Topo aoBuscar={() => abrirPaleta()} moduloAtivo={moduloAtivo} />
      <div className="faixa-abas">
        {abas.length > 0 ? <BarraAbas /> : <span className="faixa-abas-dica">As rotinas que você abrir ficam aqui, em abas. Alt+W fecha, Alt+← e Alt+→ trocam.</span>}
      </div>
      <main id="conteudo" className="shell-conteudo" tabIndex={-1}>
        <Outlet />
      </main>
      <PaletaGlobal aberta={paleta.aberta} busca={paleta.texto} aoBuscar={(texto) => setPaleta({ aberta: true, texto })} aoFechar={() => setPaleta({ aberta: false, texto: '' })} />
      <Suspense fallback={null}>
        <AvisoNovidades login={sessao.usuario.login} />
      </Suspense>
    </div>
  );
}

let abrirPaletaExterna: ((texto: string) => void) | null = null;

/** Abre a paleta global a partir de qualquer página (tela inicial, estado vazio…). */
export function abrirPaleta(texto = '') {
  abrirPaletaExterna?.(texto);
}

/** Tela inicial: digitar qualquer letra abre a busca com o caractere (07 §2.3). */
export function usePaletaComLetra(abrir: (texto: string) => void = abrirPaleta) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1 || /INPUT|TEXTAREA|SELECT/.test(t.tagName) || document.querySelector('[role="dialog"]')) return;
      if (!/[\p{L}\p{N}]/u.test(e.key)) return;
      e.preventDefault();
      abrir(e.key);
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [abrir]);
}
